import { Controller } from "@hotwired/stimulus";
import { renderAsync } from "docx-preview";

// Usage: data-controller="files-viewer"
// Provides a modal slider to view files one at a time with navigation
export default class extends Controller {
  static targets = ["modal", "content", "counter", "prevBtn", "nextBtn"];
  static values = {
    files: Array, // Array of { url: string, type: string, name: string }
  };

  currentIndex = 0;
  renderToken = 0;

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "";
  }

  open(event) {
    event?.preventDefault();
    event?.stopPropagation();
    
    // Get the starting index from the event detail or data attribute
    const startIndex = event?.detail?.index ?? event?.currentTarget?.dataset?.filesViewerIndex ?? 0;
    this.currentIndex = parseInt(startIndex, 10);

    this.modalTarget.classList.add("open");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeydown);
    
    this.renderCurrentFile();
    this.updateNavigationState();
  }

  close(event) {
    event?.preventDefault();
    
    this.modalTarget.classList.remove("open");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeydown);
    
    // Clear content when modal closes and invalidate any in-flight docx render
    this.renderToken++;
    this.contentTarget.innerHTML = "";
  }

  prev(event) {
    event?.preventDefault();
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.renderCurrentFile();
      this.updateNavigationState();
    }
  }

  next(event) {
    event?.preventDefault();
    if (this.currentIndex < this.filesValue.length - 1) {
      this.currentIndex++;
      this.renderCurrentFile();
      this.updateNavigationState();
    }
  }

  handleKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.close();
        break;
      case "ArrowLeft":
        this.prev();
        break;
      case "ArrowRight":
        this.next();
        break;
    }
  }

  renderCurrentFile() {
    const file = this.filesValue[this.currentIndex];
    if (!file) return;

    // Dispatch file-viewed event for require-file-view gating
    this.element.dispatchEvent(
      new CustomEvent("files-viewer:file-viewed", {
        bubbles: true,
        detail: { index: this.currentIndex },
      })
    );

    const type = file.type.toLowerCase();
    const renderToken = ++this.renderToken;
    let content;
    let isDocx = false;

    if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(type)) {
      // Use iframe with srcdoc for images to sandbox them securely
      const imgHtml = `<!DOCTYPE html><html><head><style>body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:transparent;}img{max-width:100%;max-height:100%;object-fit:contain;}</style></head><body><img src="${file.url}" alt="${this.escapeHtml(file.name)}"/></body></html>`;
      content = `<iframe srcdoc="${this.escapeHtml(imgHtml)}" width="100%" height="100%" frameborder="0" sandbox="allow-same-origin"></iframe>`;
    } else if (type === "pdf") {
      content = `<iframe src="${file.url}" width="100%" height="100%" frameborder="0"></iframe>`;
    } else if (type === "docx") {
      isDocx = true;
      content = `<div class="docx-preview-container">
        <div class="docx-preview-loader">
          <div class="lds-spinner" style="--size: 34px;"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
        </div>
      </div>`;
    } else {
      // Legacy .doc (binary format) and other types can't be rendered in the browser
      content = this.downloadFallbackHtml(file);
    }

    this.contentTarget.innerHTML = `
      <div class="file-viewer-header">
        <span class="file-name">${this.escapeHtml(file.name)}</span>
        <div class="file-viewer-actions">
          <a href="${file.url}" download class="app-link icon-link neutral-link download-link" title="Download">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </a>
          <button type="button" class="close-btn" data-action="click->files-viewer#close" title="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      <div class="file-viewer-body">
        ${content}
      </div>
    `;

    if (isDocx) {
      this.renderDocx(file, renderToken);
    }

    // Update counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.currentIndex + 1} / ${this.filesValue.length}`;
    }
  }

  async renderDocx(file, renderToken) {
    const container = this.contentTarget.querySelector(".docx-preview-container");
    if (!container) return;

    try {
      const response = await fetch(file.proxy_url || file.url);
      if (!response.ok) throw new Error(`Failed to fetch file (${response.status})`);
      const blob = await response.blob();

      // Bail out if the user navigated to another file or closed the modal
      if (renderToken !== this.renderToken) return;

      container.innerHTML = "";
      await renderAsync(blob, container);
    } catch (error) {
      console.error("Failed to render docx preview:", error);
      if (renderToken !== this.renderToken) return;
      container.innerHTML = this.downloadFallbackHtml(file);
    }
  }

  downloadFallbackHtml(file) {
    return `<div class="file-download-fallback">
      <p>Preview not available for this file.</p>
      <a href="${file.url}" download class="app-btn primary-btn">Download ${this.escapeHtml(file.name)}</a>
    </div>`;
  }

  updateNavigationState() {
    const hasMultipleFiles = this.filesValue.length > 1;
    
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.classList.toggle("disabled", this.currentIndex === 0);
      this.prevBtnTarget.style.display = hasMultipleFiles ? "" : "none";
    }
    
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.classList.toggle("disabled", this.currentIndex === this.filesValue.length - 1);
      this.nextBtnTarget.style.display = hasMultipleFiles ? "" : "none";
    }

    if (this.hasCounterTarget) {
      this.counterTarget.style.display = hasMultipleFiles ? "" : "none";
    }
  }

  // Called when clicking on backdrop
  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.close(event);
    }
  }

  // Escape HTML to prevent XSS when embedding in srcdoc
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML.replace(/"/g, "&quot;");
  }
}
